/**
 * LLM Utility - Client-side AI text generation
 * Priority: Anthropic (Claude) -> Gemini -> OpenAI
 */

export interface LLMPayload {
    prompt: string;
    systemPrompt?: string;
    maxTokens?: number;
    temperature?: number;
    jsonMode?: boolean;
    provider?: string;
}

export interface LLMResponse {
    success: boolean;
    content: string;
    error?: string;
    provider: 'anthropic' | 'gemini' | 'openai';
}

/**
 * Call the LLM with fallback chain
 */
export async function callLLM(payload: LLMPayload): Promise<LLMResponse> {
    // 1. Try backend proxy first
    try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/ai/generate-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: payload.prompt,
                systemPrompt: payload.systemPrompt,
                provider: payload.provider,
                jsonMode: payload.jsonMode,
                maxTokens: payload.maxTokens,
                temperature: payload.temperature
            })
        });

        if (response.ok) {
            const data = await response.json();
            return { ...data, provider: data.provider || 'anthropic' };
        }
    } catch (error) {
        console.warn('[LLM] Backend proxy error, falling back to direct calls:', error);
    }

    return {
        success: false,
        content: '',
        error: 'Backend LLM proxy unavailable',
        provider: 'anthropic'
    };
}

async function callGeminiDirect(payload: LLMPayload, apiKey: string): Promise<LLMResponse> {
    let fullPrompt = payload.prompt;
    if (payload.systemPrompt) {
        fullPrompt = `${payload.systemPrompt}\n\nUSER REQUEST: ${payload.prompt}`;
    }

    if (payload.jsonMode && !fullPrompt.toLowerCase().includes('json')) {
        fullPrompt += '\n\nIMPORTANT: Respond ONLY with valid JSON.';
    }

    const models = ['gemini-2.5-flash', 'gemini-2.0-flash-001'];

    for (const model of models) {
        try {
            const apiVersion = 'v1beta';
            const url = `https://generativelanguage.googleapis.com/${apiVersion}/models/${model}:generateContent?key=${apiKey}`;

            const requestBody: any = {
                contents: [{ parts: [{ text: fullPrompt }] }],
                generationConfig: {
                    maxOutputTokens: payload.maxTokens || 1000,
                    temperature: payload.temperature || 0.7
                }
            };

            if (payload.jsonMode) {
                requestBody.generationConfig.response_mime_type = 'application/json';
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) continue;

            const data = await response.json();
            const content = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

            if (content) {
                return { success: true, content: content.trim(), provider: 'gemini' };
            }
        } catch {
            continue;
        }
    }

    throw new Error('All Gemini models failed');
}

export async function callLLMStream(payload: LLMPayload, onToken: (text: string) => void): Promise<LLMResponse> {
    try {
        const result = await callLLM(payload);
        if (result.success) {
            onToken(result.content);
        }
        return result;
    } catch (e: any) {
        return { success: false, content: '', error: e.message, provider: 'anthropic' };
    }
}
